/* ============================================================
   VELOURIA — Animation Engine
   Scroll-driven progressive fade (Kendall's standard).
   Intersection Observer for reveal, scroll listener for parallax.
   ============================================================ */
(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Reveal on scroll ─────────────────────────────────────────
  const revealEls = () => document.querySelectorAll('.vlr-reveal, .vlr-reveal-stagger, .vlr-reveal-words');
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          revealObserver.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  const initReveals = () => {
    revealEls().forEach((el) => {
      if (prefersReduced) {
        el.classList.add('is-in');
      } else {
        revealObserver.observe(el);
      }
    });
  };

  // ── Word/letter splitter for hero typography reveals ────────
  const splitWords = (root) => {
    root.querySelectorAll('[data-vlr-split]').forEach((el) => {
      if (el.dataset.vlrSplitDone) return;
      const text = el.textContent.trim();
      const words = text.split(/\s+/);
      el.innerHTML = words
        .map((w, i) => `<span class="vlr-reveal-letter" style="transition-delay:${i * 90}ms">${w}</span>`)
        .join(' ');
      el.dataset.vlrSplitDone = '1';
    });
  };

  // ── Parallax (translateY based on scroll, no fixed bg) ──────
  let parallaxTargets = [];
  const refreshParallax = () => {
    parallaxTargets = Array.from(document.querySelectorAll('[data-vlr-parallax]')).map((el) => ({
      el,
      speed: parseFloat(el.dataset.vlrParallax) || 0.2,
    }));
  };
  const updateParallax = () => {
    if (prefersReduced) return;
    const vh = window.innerHeight;
    parallaxTargets.forEach(({ el, speed }) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      const center = rect.top + rect.height / 2;
      const offset = (center - vh / 2) * speed * -1;
      el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    });
  };

  // ── Sticky header scroll state ──────────────────────────────
  // Target the section that wraps .header-wrapper (the actual header, not the announcement bar)
  const headerWrapper = document.querySelector('.header-wrapper');
  const stickyHeader = headerWrapper ? headerWrapper.closest('.shopify-section') : null;
  const updateHeader = () => {
    if (!stickyHeader) return;
    if (window.scrollY > 24) stickyHeader.classList.add('vlr-scrolled');
    else stickyHeader.classList.remove('vlr-scrolled');
  };

  // ── Marquee duplicator ──────────────────────────────────────
  const initMarquee = () => {
    document.querySelectorAll('[data-vlr-marquee]').forEach((m) => {
      if (m.dataset.vlrMarqueeDone) return;
      const track = m.querySelector('.vlr-marquee__track');
      if (!track) return;
      const inner = track.innerHTML;
      track.innerHTML = inner + inner; // duplicate for seamless loop
      m.dataset.vlrMarqueeDone = '1';
    });
  };

  // ── Hero scroll dim (cinematic darken on scroll past hero) ──
  const initHeroScroll = () => {
    const hero = document.querySelector('[data-vlr-hero]');
    if (!hero) return;
    const media = hero.querySelector('[data-vlr-hero-media]');
    const overlay = hero.querySelector('[data-vlr-hero-overlay]');
    const content = hero.querySelector('[data-vlr-hero-content]');
    const onScroll = () => {
      const rect = hero.getBoundingClientRect();
      const h = hero.offsetHeight || 1;
      const progress = Math.max(0, Math.min(1, -rect.top / h));
      if (media) media.style.transform = `translate3d(0, ${(progress * 80).toFixed(1)}px, 0) scale(${1 + progress * 0.05})`;
      if (overlay) overlay.style.opacity = (0.28 + progress * 0.5).toFixed(3);
      if (content) {
        content.style.opacity = (1 - progress * 1.4).toFixed(3);
        content.style.transform = `translate3d(0, ${(progress * -40).toFixed(1)}px, 0)`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  // ── Image lazy reveal on load ───────────────────────────────
  const initImageFade = () => {
    document.querySelectorAll('img[data-vlr-fade]').forEach((img) => {
      if (img.complete) img.classList.add('is-loaded');
      else img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
    });
  };

  // ── rAF scroll loop ─────────────────────────────────────────
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateParallax();
      updateHeader();
      ticking = false;
    });
  };

  // ── Boot ────────────────────────────────────────────────────
  const boot = () => {
    splitWords(document);
    initReveals();
    refreshParallax();
    initMarquee();
    initHeroScroll();
    initImageFade();
    updateHeader();
    updateParallax();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    refreshParallax();
    updateParallax();
  });

  // Re-init when Shopify theme editor injects sections
  document.addEventListener('shopify:section:load', () => {
    setTimeout(boot, 50);
  });
})();
